/* ASSET - assets/coco-product-gallery.js */

/* Dots and the stacked thumbnail rail for the product gallery.

   SliderComponent (global.js) owns the scrolling, the arrows and the counter -
   it binds to [id^="Slider-"], button[name="previous"], button[name="next"],
   .slider-counter--current and .slider-counter--total, all of which the gallery
   markup provides. It has no dot support, so that is all this file adds.

   It listens for SliderComponent's own 'slideChanged' event rather than
   scroll, so the two can never disagree about the current slide.

   setActiveMedia exists because VariantSelects.updateMedia (global.js:1000)
   calls it on whatever matches [id^="MediaGallery-"]. Without it, emitting that
   id throws "setActiveMedia is not a function" on every variant change.

   goTo has two paths. In slider mode it scrolls the TRACK, exactly as it
   always has. In stacked mode the track has no horizontal axis - every slide
   shares an offsetLeft - so it scrolls the PAGE to the slide instead. The
   slider path is untouched; stacked is an extra branch in front of it.

   The thumbnail rail is present only when the merchant has added the
   coco_gallery_rail block. Without it this.thumbs is empty, no observer is
   created, and every rail branch below is one length check from a no-op. */

class CocoGalleryDots extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('slider-component')
    this.track = this.querySelector('[id^="Slider-CocoGallery-"]')
    this.slides = Array.from(this.querySelectorAll('[id^="Slide-"]'))
    this.dotsWrap = this.querySelector('[data-coco-gallery-dots]')
    this.dots = []
    this.thumbs = []
    this.activeIndex = 1
    this.suppressObserver = false
    this.scrollWatchdog = 0
    if (!this.slider) return

    /* Rail setup sits ABOVE the dots guard further down, deliberately. That
       guard returns on anything with fewer than two dots, and the dots only
       render when media.size > 1 - so anything set up below it is dead on
       single-image products, and would go dead everywhere if the dots were
       ever moved behind a different condition. The rail's own markup is
       gated on media_count > 1 in the snippet, which makes this ordering
       redundant today; it is here so the JS stays correct on its own terms
       rather than on the snippet's. */
    this.rail = this.querySelector('[data-coco-gallery-rail]')
    if (this.rail) this.thumbs = Array.from(this.rail.querySelectorAll('[data-coco-gallery-thumb]'))

    if (this.thumbs.length) {
      this.onThumbClick = this.onThumbClick.bind(this)
      this.onThumbKeydown = this.onThumbKeydown.bind(this)
      this.onIntersect = this.onIntersect.bind(this)
      this.onScrollSettled = this.onScrollSettled.bind(this)

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', this.onThumbClick)
        thumb.addEventListener('keydown', this.onThumbKeydown)
      })

      /* The observer is a TRIGGER, not the decision. It says "the column
         moved"; syncFromViewport says which slide that leaves at the top of
         the reading area. Keeping the decision in one place is what lets the
         click path, the variant path and the scroll path agree by
         construction rather than by luck.

         rootMargin is the old page's (custom.js:220). threshold is NOT: it
         used 0.2, which never fires for a target more than five times the
         height of the band, so one very tall image would silently stop
         updating the rail. At 0 every crossing of a band edge fires, and
         since the entries are never read the extra callbacks cost one cheap
         re-decide each. */
      this.observer = new IntersectionObserver(this.onIntersect, {
        root: null,
        rootMargin: '-20% 0px -20% 0px',
        threshold: 0
      })

      this.slides.forEach((slide) => this.observer.observe(slide))
    }

    this.dots = this.dotsWrap ? Array.from(this.dotsWrap.querySelectorAll('[data-coco-gallery-dot]')) : []
    if (this.dots.length < 2) return

    this.onSlideChanged = this.onSlideChanged.bind(this)
    this.onDotClick = this.onDotClick.bind(this)

    this.slider.addEventListener('slideChanged', this.onSlideChanged)
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick))
  }

  disconnectedCallback() {
    if (this.slider) this.slider.removeEventListener('slideChanged', this.onSlideChanged)
    if (this.dots) this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick))
    if (this.observer) this.observer.disconnect()

    if (this.thumbs) {
      this.thumbs.forEach((thumb) => {
        thumb.removeEventListener('click', this.onThumbClick)
        thumb.removeEventListener('keydown', this.onThumbKeydown)
      })
    }

    if (this.scrollWatchdog) clearTimeout(this.scrollWatchdog)
    window.removeEventListener('scrollend', this.onScrollSettled)
  }

  onSlideChanged(event) {
    this.setActive(event.detail.currentPage)
  }

  onDotClick(event) {
    this.goTo(parseInt(event.currentTarget.dataset.cocoGalleryDot, 10))
  }

  /* A thumb click goes through goTo like everything else. There is
     deliberately no second scroll path here: the stacked branch already owns
     the header offset, the in-view backstop and prefers-reduced-motion. */
  onThumbClick(event) {
    this.goTo(parseInt(event.currentTarget.dataset.cocoGalleryThumb, 10))
  }

  /* Roving tabindex. One tab stop for the whole rail, and the thumb carrying
     tabindex 0 is always the one carrying aria-current because setActive
     writes both. Arrows move focus AND activate, which is what a picker
     whose only job is to move the view should do; Home and End jump to the
     ends; both axes are accepted because the rail is a column but the images
     it points at read as a horizontal control to most people. The ends wrap.
     Enter and Space need nothing - this is a native button. */
  onThumbKeydown(event) {
    const keys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End']
    if (keys.indexOf(event.key) === -1) return

    event.preventDefault()

    const last = this.thumbs.length
    const from = this.thumbs.indexOf(event.currentTarget) + 1
    if (from < 1 || last < 1) return

    let next = from
    if (event.key === 'Home') next = 1
    else if (event.key === 'End') next = last
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = from > 1 ? from - 1 : last
    else next = from < last ? from + 1 : 1

    this.goTo(next)
    this.thumbs[next - 1].focus()
  }

  /* The observer decides nothing itself - see the note where it is built.
     While a scroll we started is in flight it is ignored outright, which is
     what stops a click or a variant change from strobing the highlight
     through every slide it passes on the way. */
  onIntersect() {
    if (this.suppressObserver) return

    this.syncFromViewport()
  }

  /* Which slide sits at the top of the reading area, decided by the same
     rule the scroll uses to put one there: the first slide whose bottom is
     still below the offset line. Nearest-to-centre was the obvious
     alternative and is wrong - a short slide scrolled to the top sits
     entirely above centre, so centre-matching would light the slide AFTER
     the one just clicked. */
  syncFromViewport() {
    if (!this.thumbs.length || !this.isStacked()) return

    const line = this.scrollOffset() + 1
    let index = this.slides.length

    for (let i = 0; i < this.slides.length; i++) {
      if (this.slides[i].getBoundingClientRect().bottom > line) {
        index = i + 1
        break
      }
    }

    if (index !== this.activeIndex) this.setActive(index)
  }

  goTo(index) {
    const slide = this.slides[index - 1]
    if (!slide || !this.track) return

    if (this.isStacked()) {
      this.scrollPageTo(slide)
      this.setActive(index)
      return
    }

    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' })
    this.setActive(index)
  }

  /* Branched here rather than in setActiveMedia because goTo is the one place
     that owns "move the view to slide N" - setActiveMedia, onDotClick,
     onThumbClick and onThumbKeydown all want that meaning, and a future
     caller would get it too. Putting it in setActiveMedia would leave goTo a
     silent no-op in stacked mode.

     Read at call time, never cached or captured at bind time: the wrapper
     carries .coco-pdp--stacked at every breakpoint while the stacked CSS only
     applies from 990px, so both halves have to be true - and the theme editor
     changes the setting without a reload while the viewport resizes under us. */
  isStacked() {
    if (!this.closest('.coco-pdp--stacked')) return false

    return window.matchMedia('(min-width: 990px)').matches
  }

  /* Page scroll for the stacked column.

     No load-time guard is needed and none is added. setActiveMedia is reached
     only from VariantSelects.updateMedia (global.js:995), which is called only
     from onVariantChange, which is bound to the picker's own 'change' event in
     the constructor - there is no initial-render call. renderProductInfo does
     NOT call updateMedia; it publishes PUB_SUB_EVENTS.variantChange, and none
     of the five subscribers (coco-bespoke-mode, coco-sticky-atc,
     price-per-item, product-info, recipient-form) touches a media gallery.
     media-gallery.js has its own setActiveMedia but that is Dawn's
     <media-gallery>, which this PDP does not render. The in-view test below is
     the backstop regardless: on load the top slide is on screen, so a first
     call could not move the page even if one ever arrived. */
  scrollPageTo(slide) {
    const offset = this.scrollOffset()
    const rect = slide.getBoundingClientRect()
    const fullyVisible = rect.top >= offset && rect.bottom <= window.innerHeight
    const fillsViewport = rect.top <= offset && rect.bottom >= window.innerHeight

    if (fullyVisible || fillsViewport) return

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    this.beginProgrammaticScroll()
    window.scrollTo({ top: rect.top + window.pageYOffset - offset, behavior: reduce ? 'auto' : 'smooth' })
  }

  /* Observer suppression, held only for as long as a scroll we started.

     scrollend is the right signal and is feature-detected rather than
     assumed: Chrome and Firefox ship it, WebKit does not, so on Safari it
     would never arrive and the rail would stay frozen for the life of the
     page. The watchdog is therefore armed in BOTH branches, not only the
     fallback one - a browser that has scrollend can still withhold it when a
     smooth scroll is interrupted, or when the target position was already
     current and no scroll ever started. Whichever fires first wins;
     onScrollSettled is idempotent and clears the other. */
  beginProgrammaticScroll() {
    if (!this.observer) return

    this.suppressObserver = true

    if (this.scrollWatchdog) clearTimeout(this.scrollWatchdog)
    if ('onscrollend' in window) window.addEventListener('scrollend', this.onScrollSettled, { once: true })

    this.scrollWatchdog = setTimeout(this.onScrollSettled, 1200)
  }

  /* Re-seat from the DOM rather than trusting the index we asked for: a
     smooth scroll can land somewhere else entirely if the visitor scrolls,
     resizes, or clicks another thumb while it is still running. */
  onScrollSettled() {
    this.suppressObserver = false

    if (this.scrollWatchdog) {
      clearTimeout(this.scrollWatchdog)
      this.scrollWatchdog = 0
    }

    window.removeEventListener('scrollend', this.onScrollSettled)
    this.syncFromViewport()
  }

  setActiveMedia(mediaId) {
    const slide = this.querySelector(`[data-media-id="${mediaId}"]`)
    if (!slide) return

    this.goTo(this.slides.indexOf(slide) + 1)
  }

  /* The single owner of active state. Everything reaches it: onSlideChanged
     in slider mode, goTo for a dot click, a thumb click, a keyboard move and
     setActiveMedia, and syncFromViewport while the stacked column scrolls.
     The dots' class, the rail's aria-current and the rail's roving tabindex
     are all written here and nowhere else - the tabindex especially, because
     a second writer is exactly how focus and highlight come to disagree.

     aria-current is set and cleared in one pass per thumb rather than Dawn's
     clear-all-then-set (media-gallery.js:67-70). Same end state, one
     traversal, and no window in which nothing is current. */
  setActive(index) {
    if (index < 1) return

    this.activeIndex = index

    if (this.dots) {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index - 1)
      })
    }

    if (!this.thumbs || !this.thumbs.length) return

    this.thumbs.forEach((thumb, i) => {
      const on = i === index - 1

      if (on) thumb.setAttribute('aria-current', 'true')
      else thumb.removeAttribute('aria-current')

      thumb.tabIndex = on ? 0 : -1
    })
  }

  /* --header-height is written onto <html> by sections/header.liquid:513, but
     only from its scroll handler, so it can still be empty on a page nobody
     has scrolled yet - hence the 96px fallback, the same one the sticky rules
     in coco-product-gallery.css use. Reading it here is safe: the notes'
     warning is about using this value inside a height calc(), where a
     mid-transition reading sticks; a scroll offset is computed once and
     discarded. --coco-gallery-offset is the merchant's "Sticky top offset",
     reused so the resting gap matches the sticky one. */
  scrollOffset() {
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'))
    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--coco-gallery-offset'))

    return (isNaN(header) ? 96 : header) + (isNaN(gap) ? 16 : gap)
  }
}

if (!customElements.get('coco-gallery-dots')) {
  customElements.define('coco-gallery-dots', CocoGalleryDots)
}
